import type { MailboxInfo } from 'owa-client-types';
import { getAccountScopeUserSettings } from 'owa-session-store';
import { makeGetRequest } from 'owa-ows-gateway';

export interface GraphMeBasicInfoResult {
    displayName?: string;
    photoObjectUrl?: string; // object URL for caller to revoke
    photoFetchStatus?: 'loaded' | 'notFound' | 'error';
}

/**
 * Fetch the current user's displayName (Graph /me?$select=displayName) and optional profile photo.
 * Returns both the displayName (if available) and an object URL for the photo.
 * Caller must revoke the object URL when no longer needed.
 */
export interface GetDisplayNameAndPhotoOptions {
    fetchPhoto?: boolean; // default true
    fetchDisplayName?: boolean; // default true
    signal?: AbortSignal; // optional cancellation
}

/**
 * Improved helper with:
 *  - Optional selective fetching (display name, photo)
 *  - Parallel network requests
 *  - AbortSignal support
 *  - Size validation (clamps to allowed preset)
 */
export async function getDisplayNameAndPhoto(
    mailboxInfo: MailboxInfo,
    size: 48 | 64 | 96 = 48,
    options: GetDisplayNameAndPhotoOptions = {}
): Promise<GraphMeBasicInfoResult> {
    const { fetchPhoto = true, fetchDisplayName = true, signal } = options;
    const result: GraphMeBasicInfoResult = {};

    // Clamp size to nearest allowed (48, 64, 96) if an invalid literal sneaks in via casting.
    if (size !== 48 && size !== 64 && size !== 96) {
        size = 48;
    }

    if (!fetchPhoto && !fetchDisplayName) {
        return result; // nothing to do
    }

    try {
        // Build promises array conditionally for parallel execution
        const promises: Array<Promise<void>> = [];

        if (fetchDisplayName) {
            promises.push(
                (async () => {
                    if (signal?.aborted) {
                        return;
                    }
                    const dn = await fetchDisplayNameInternal(mailboxInfo);
                    if (dn && !signal?.aborted) {
                        result.displayName = dn;
                    }
                })()
            );
        }

        if (fetchPhoto) {
            promises.push(
                (async () => {
                    if (signal?.aborted) {
                        return;
                    }
                    const photoResult = await fetchProfilePhotoObjectUrl(mailboxInfo, size, signal);
                    if (!signal?.aborted) {
                        result.photoFetchStatus = photoResult.photoFetchStatus;
                        if (photoResult.photoObjectUrl) {
                            result.photoObjectUrl = photoResult.photoObjectUrl;
                        }
                    }
                })()
            );
        }

        if (promises.length) {
            // Use allSettled to allow partial results
            await Promise.allSettled(promises);
        }
    } catch {
        // swallow errors; partial result (or empty) returned
    }

    return result;
}

/** Fetch displayName from session store with fallback to user identity */
async function fetchDisplayNameInternal(mailboxInfo: MailboxInfo): Promise<string | undefined> {
    try {
        // Primary: Get display name from session store (startup data)
        const userSettings = getAccountScopeUserSettings(mailboxInfo);
        const displayName = userSettings?.SessionSettings?.UserDisplayName;

        if (displayName) {
            return displayName;
        }
    } catch {
        // Continue to fallback
    }

    // Fallback to user identity (will be email address)
    return mailboxInfo.userIdentity;
}

/** Retrieve profile photo URL using PeopleGraphVx endpoint */
async function fetchProfilePhotoObjectUrl(
    mailboxInfo: MailboxInfo,
    size: number,
    signal?: AbortSignal
): Promise<Pick<GraphMeBasicInfoResult, 'photoObjectUrl' | 'photoFetchStatus'>> {
    try {
        if (signal?.aborted) {
            return { photoFetchStatus: 'error' };
        }

        const userEmail = mailboxInfo.userIdentity;
        const photoUrl = `/PeopleGraphVx/v1.0/people/SMTP:${userEmail}/fetchResizedPhoto(height=${size},width=${size},allowResizeUp=true)/$value`;

        // Use makeGetRequest with isHttpCachingExpected: true to prevent n= and cv= query parameters
        const response = await makeGetRequest<Blob, true>(
            photoUrl,
            undefined, // correlationId
            true, // returnFullResponse
            {}, // customHeaders
            undefined, // throwServiceError
            true, // includeCredentials
            'FetchProfilePhoto', // actionName
            mailboxInfo,
            true // isHttpCachingExpected - this prevents n= and cv= query parameters
        );

        if (response.ok && !signal?.aborted) {
            const blob = await response.blob();
            return {
                photoObjectUrl: URL.createObjectURL(blob),
                photoFetchStatus: 'loaded',
            };
        }

        return { photoFetchStatus: response.status === 404 ? 'notFound' : 'error' };
    } catch {
        // If any error occurs, fallback gracefully
        return { photoFetchStatus: 'error' };
    }
}
